import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useRevealViewport } from '../useRevealViewport';

interface DepthRevealLayerProps {
  /** Activate the layer once the message text becomes visible */
  active: boolean;
  /** Tint color for foreground particles + light sweep, e.g. 'rgba(251,191,36,0.6)' */
  tint: string;
  /** Optional secondary tint for the trailing sweep */
  glow?: string;
  /** Vertical anchor (0-1) for foreground particle origin. Default 0.5 */
  originY?: number;
}

/**
 * Phase 2 — Depth + Dynamic Lighting overlay.
 *
 * Adds three composited layers ABOVE the scene's existing visuals:
 *   1. Foreground particle plane (drifts toward viewer, parallax feel)
 *   2. Light sweep across the headline area (single timed pass)
 *   3. Subtle edge vignette to anchor depth
 *
 * Pure CSS / Framer Motion. No WebGL. GPU-only props (transform/opacity/filter).
 * Particle counts halve on mobile via useRevealViewport.particleScale.
 */
export function DepthRevealLayer({ active, tint, glow, originY = 0.5 }: DepthRevealLayerProps) {
  const { isMobile, particleScale } = useRevealViewport();

  const particles = useMemo(() => {
    const count = Math.round((isMobile ? 6 : 12) * particleScale);
    return Array.from({ length: count }, (_, i) => ({
      key: `fg-${i}`,
      x: 5 + Math.random() * 90,
      yStart: 60 + Math.random() * 40,
      yEnd: -10 - Math.random() * 20,
      size: 2 + Math.random() * 3,
      duration: 9 + Math.random() * 6,
      delay: Math.random() * 4,
      opacity: 0.35 + Math.random() * 0.45,
      blur: Math.random() > 0.5 ? 1 : 0,
    }));
  }, [isMobile, particleScale]);

  const sweepGlow = glow ?? tint;

  return (
    <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden z-20">
      {/* 1. Foreground floating particles — slight blur for depth */}
      {active && particles.map((p) => (
        <motion.div
          key={p.key}
          className="absolute rounded-full"
          style={{
            width: p.size,
            height: p.size,
            left: `${p.x}%`,
            top: `${p.yStart}%`,
            background: tint,
            boxShadow: `0 0 ${p.size * 5}px ${p.size}px ${tint}`,
            filter: p.blur ? 'blur(1px)' : undefined,
            willChange: 'transform, opacity',
          }}
          initial={{ opacity: 0, y: 0 }}
          animate={{
            opacity: [0, p.opacity, p.opacity * 0.4, 0],
            y: [`0%`, `${p.yEnd - p.yStart}%`],
            scale: [0.8, 1.1, 1, 0.9],
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      ))}

      {/* 2. Headline light sweep — one elegant pass when text reveals */}
      {active && (
        <>
          <motion.div
            className="absolute z-10"
            style={{
              top: `${originY * 100 - 8}%`,
              height: '18%',
              width: '60%',
              background: `linear-gradient(105deg, transparent 30%, ${sweepGlow} 50%, transparent 70%)`,
              filter: 'blur(14px)',
              mixBlendMode: 'screen',
              willChange: 'transform, opacity',
            }}
            initial={{ x: '-120%', opacity: 0 }}
            animate={{ x: '220%', opacity: [0, 0.9, 0] }}
            transition={{ duration: 2.4, delay: 1.2, ease: [0.22, 0.61, 0.36, 1] }}
          />
          <motion.div
            className="absolute z-10"
            style={{
              top: `${originY * 100 - 4}%`,
              height: '10%',
              width: '40%',
              background: `linear-gradient(105deg, transparent 35%, ${sweepGlow} 50%, transparent 65%)`,
              filter: 'blur(6px)',
              mixBlendMode: 'screen',
              willChange: 'transform, opacity',
            }}
            initial={{ x: '-80%', opacity: 0 }}
            animate={{ x: '200%', opacity: [0, 0.7, 0] }}
            transition={{ duration: 2.0, delay: 1.4, ease: [0.22, 0.61, 0.36, 1] }}
          />
        </>
      )}

      {/* 3. Soft edge vignette for cinematic depth anchoring */}
      <motion.div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.35) 100%)',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: active ? 1 : 0 }}
        transition={{ duration: 1.5 }}
      />
    </div>
  );
}
